import Rete from "rete";
import {
    DefaultSequenceComponent,
    SequenceComponent,
    StartComponent,
    EndComponent
} from "../components";
import {SequenceComponentHelper} from "./SequenceComponentHelper";
import {VideoMapping} from "../../models/VideoMapping";
import {StereoMode} from "../../models/StereoMode";
import {NodeType} from "../../models/NodeType";

Rete.Node.prototype.customToJSON = function (node) {
    switch(node.name) {
        case SequenceComponent.COMPONENT_NAME:
            return sequenceNodeToJson(node);
        case DefaultSequenceComponent.COMPONENT_NAME:
            return defaultSequenceNodeToJson(node);
        case StartComponent.COMPONENT_NAME:
            return startNodeToJson(node);
        case EndComponent.COMPONENT_NAME:
            return endNodeToJson(node);
        default:
            throw new Error(`Unknown component name: "${node.name}". Parsing to custom JSON can't be finished.`);
    }
}

function sequenceNodeToJson(node) {

    // controls
    let nodeData = {
        video: null,
        textQuestion: null,
        voiceAnswers: null,
        textAnswers: null,
        image: null,
        timeLimit: null,
        textSlide: null,
        audio: null,
        infos: null,
    };
    const {videoId, textQuestionId, voiceAnswerIds, defaultVoiceAnswerId, voiceAnswerTimeLimitId, textAnswerIds, textAnswerTimeLimitId, imageId, textSlideId, audioId, infoIds} = SequenceComponentHelper.getDataIds(node.data);

    let hasAnswer = SequenceComponentHelper.hasAnswer(node);

    if (videoId !== null) {

        let videoMapping = VideoMapping.Normal; // TODO: make somewhere default value and assign here and in Video.addVideo
        const mappingVal = node.data[SequenceComponentHelper.getVideoMappingKey(videoId)];
        if (mappingVal) {
            switch (mappingVal) {
                case "normal":
                    videoMapping = VideoMapping.Normal;
                    break;
                case "180":
                    videoMapping = VideoMapping.OneEighty;
                    break;
                case "360":
                    videoMapping = VideoMapping.ThreeSixty;
                    break;
                default:
                    throw new Error("Unhandled video mapping type.");
            }
        }

        let stereoMode = StereoMode.None; // TODO: make somewhere default value and assign here and in Video.addVideo
        const stereoVal = node.data[SequenceComponentHelper.getVideoStereoModeKey(videoId)]
        if (stereoVal) {
            switch(stereoVal) {
                case "none":
                    stereoMode = StereoMode.None;
                    break;
                case "left-right":
                    stereoMode = StereoMode.LeftRight;
                    break;
                case "top-bottom":
                    stereoMode = StereoMode.TopBottom;
                    break;
                default:
                    throw new Error("Unhandled stereo mode.");
            }
        }


        nodeData.video = {
            // id: videoId,
            fileId: node.data[SequenceComponentHelper.getVideoFileKey(videoId)],
            videoMapping: videoMapping,
            stereoMode: stereoMode,
            startFrame: node.data[SequenceComponentHelper.getVideoStartFrameKey(videoId)],
            stopFrame: node.data[SequenceComponentHelper.getVideoStopFrameKey(videoId)],
            hasAnswer: hasAnswer,
            nodeOutputId: videoId
        };
    }

    if (imageId !== null) {
        nodeData.image = {
            fileId: node.data[SequenceComponentHelper.getImageFileKey(imageId)],
            duration: node.data[SequenceComponentHelper.getImageDurationKey(imageId)],
            hasAnswer: hasAnswer,
            nodeOutputId: imageId
        }
    }

    if (textQuestionId !== null) {
        nodeData.textQuestion = node.data[SequenceComponentHelper.getTextQuestionQuestionKey(textQuestionId)];
    }

    nodeData.voiceAnswers = voiceAnswerIds.length > 0 ? [] : null;
    for(let i = 0; i < voiceAnswerIds.length; i++) {
        let separator = ';'
        let keywordsString = node.data[SequenceComponentHelper.getVoiceAnswerKeywordKey(voiceAnswerIds[i])];

        if(keywordsString.startsWith(separator)) {
            keywordsString = keywordsString.substring(1);
        }

        if(keywordsString.endsWith(separator)) {
            keywordsString = keywordsString.substring(0, keywordsString.length - 1);
        }

        nodeData.voiceAnswers.push({
            // id: voiceAnswerIds[i],
            keywords: keywordsString,
            score: node.data[SequenceComponentHelper.getVoiceAnswerScoreKey(voiceAnswerIds[i])],
            isDefault: false,
            nodeOutputId: voiceAnswerIds[i] // todo: get id from node.outputs?
        });
    }

    if (defaultVoiceAnswerId !== null) {
        nodeData.voiceAnswers.push({
            // id: defaultVoiceAnswerId,
            keywords: null,
            score: node.data[SequenceComponentHelper.getDefaultVoiceAnswerScoreKey(defaultVoiceAnswerId)],
            isDefault: true,
            nodeOutputId: defaultVoiceAnswerId // todo: get id from node.outputs?
        });

    }

    if (voiceAnswerTimeLimitId !== null) {
        nodeData.timeLimit = node.data[SequenceComponentHelper.getVoiceAnswerTimeLimitKey(voiceAnswerTimeLimitId)];
    }

    nodeData.textAnswers = textAnswerIds.length > 0 ? [] : null;
    for(let i = 0; i < textAnswerIds.length; i++) {
        nodeData.textAnswers.push({
            // id: textAnswerIds[i],
            answer: node.data[SequenceComponentHelper.getTextAnswerAnswerKey(textAnswerIds[i])],
            score: node.data[SequenceComponentHelper.getTextAnswerScoreKey(textAnswerIds[i])],
            order: node.data[SequenceComponentHelper.getTextAnswerOrderKey(textAnswerIds[i])],
            nodeOutputId: textAnswerIds[i] // todo: get id from node.outputs?
        });
    }

    if (textAnswerTimeLimitId !== null) {
        nodeData.timeLimit = node.data[SequenceComponentHelper.getTextAnswerTimeLimitKey(textAnswerTimeLimitId)];
    }

    if (textSlideId !== null) {
        const header = node.data[SequenceComponentHelper.getTextSlideHeaderKey(textSlideId)].trim();
        nodeData.textSlide = {
            header: header.length > 0 ? header : null,
            text: node.data[SequenceComponentHelper.getTextSlideTextKey(textSlideId)],
            duration: node.data[SequenceComponentHelper.getTextSlideDurationKey(textSlideId)],
            hasAnswer: hasAnswer,
            nodeOutputId: textSlideId
        }
    }

    if (audioId !== null) {
        nodeData.audio = {
            fileId: node.data[SequenceComponentHelper.getAudioFileKey(audioId)]
        }
    }

    nodeData.infos = infoIds.length > 0 ? [] : null;
    for(let i = 0; i < infoIds.length; i++) {
        nodeData.infos.push({
            // id: infoIds[i],
            fileId: node.data[SequenceComponentHelper.getInfoIconFileKey(infoIds[i])],
            text: node.data[SequenceComponentHelper.getInfoTextKey(infoIds[i])]
        });
    }

    const nodeId = node.componentId;

    return {
        // 'id': node.id,
        data: nodeData,
        name: node.name,
        customName: (!node.customName || node.customName.length === 0) ? null : node.customName,
        type: NodeType.Sequence,
        nodeInputId: nodeId,
        defaultOutputId: nodeId
    }
}

function defaultSequenceNodeToJson(node) {

    // controls
    let nodeData = {video: null};
    const {videoId} = SequenceComponentHelper.getDataIds(node.data);

    if (videoId !== null) {
        let videoMapping = VideoMapping.Normal; // TODO: make somewhere default value and assign here and in Video.addVideo
        const mappingVal = node.data[SequenceComponentHelper.getVideoMappingKey(videoId)];
        if (mappingVal) {
            switch (mappingVal) {
                case "normal":
                    videoMapping = VideoMapping.Normal;
                    break;
                case "180":
                    videoMapping = VideoMapping.OneEighty;
                    break;
                case "360":
                    videoMapping = VideoMapping.ThreeSixty;
                    break;
                default:
                    throw new Error("Unhandled video mapping type.");
            }
        }


        let stereoMode = StereoMode.None; // TODO: make somewhere default value and assign here and in Video.addVideo
        const stereoVal = node.data[SequenceComponentHelper.getVideoStereoModeKey(videoId)];
        if (stereoVal) {
            switch(stereoVal) {
                case "none":
                    stereoMode = StereoMode.None;
                    break;
                case "left-right":
                    stereoMode = StereoMode.LeftRight;
                    break;
                case "top-bottom":
                    stereoMode = StereoMode.TopBottom;
                    break;
                default:
                    throw new Error("Unhandled stereo mode.");
            }
        }

        nodeData.video = {
            // id: videoId,
            fileId: node.data[SequenceComponentHelper.getVideoFileKey(videoId)],
            videoMapping: videoMapping,
            stereoMode: stereoMode,
            startFrame: node.data[SequenceComponentHelper.getVideoStartFrameKey(videoId)],
            stopFrame: node.data[SequenceComponentHelper.getVideoStopFrameKey(videoId)],
            hasAnswer: false,
            nodeOutputId: videoId
        };
    }

    let nodeType = NodeType.Sequence;
    const nodeId = node.componentId;

    return {
        // 'id': node.id,
        data: nodeData,
        name: node.name,
        customName: (!node.customName || node.customName.length === 0) ? null : node.customName,
        type: nodeType,
        nodeInputId: nodeId,
        defaultOutputId: nodeId
    }
}

function startNodeToJson(node) {

    // controls
    // none

    const nodeId = node.componentId;

    return {
        // 'id': node.id,
        data: null,
        name: node.name,
        customName: (!node.customName || node.customName.length === 0) ? null : node.customName,
        type: NodeType.Start,
        nodeInputId: nodeId,
        defaultOutputId: nodeId
    }
}

function endNodeToJson(node) {

    // controls
    // none

    const nodeId = node.componentId;

    return {
        // 'id': node.id,
        data: null,
        name: node.name,
        customName: (!node.customName || node.customName.length === 0) ? null : node.customName,
        type: NodeType.End,
        nodeInputId: nodeId,
        defaultOutputId: nodeId
    }
}